import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import '@material/web/dialog/dialog.js'
import '@material/web/button/text-button.js'
import '@material/web/button/filled-button.js'
import '@material/web/switch/switch.js'
import type { MdDialog } from '@material/web/dialog/dialog.js'

export interface ContactShareChoices {
  email?: string
  phone?: string
  emergencyContact?: { name: string; phone: string; relationship: string }
}

interface UserContactData {
  email?: string
  phone?: string
  emergencyContact?: { name: string; phone: string; relationship: string }
}

@customElement('contact-share-dialog')
export class ContactShareDialog extends LitElement {
  static override styles = css`
    :host {
      position: fixed;
      z-index: 3000;
      pointer-events: none;
    }

    md-dialog {
      --md-dialog-container-color: #1e293b;
      --md-dialog-headline-color: white;
      --md-dialog-supporting-text-color: rgba(255, 255, 255, 0.8);
      --md-sys-color-outline: rgba(255, 255, 255, 0.2);
      --md-dialog-container-shape: 12px;
      pointer-events: all;
      max-width: 420px;
    }

    md-text-button {
      --md-text-button-label-text-color: rgba(255, 255, 255, 0.7);
      --md-text-button-hover-label-text-color: white;
    }

    md-filled-button {
      --md-filled-button-container-color: #3b82f6;
      --md-filled-button-label-text-color: white;
    }

    .share-option {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .share-option:last-child {
      border-bottom: none;
    }

    .option-info {
      flex: 1;
    }

    .option-label {
      color: white;
      font-weight: 500;
      font-size: 14px;
    }

    .option-value {
      color: rgba(255, 255, 255, 0.5);
      font-size: 13px;
      margin-top: 2px;
    }

    .option-missing {
      color: rgba(255, 255, 255, 0.3);
      font-style: italic;
      font-size: 13px;
      margin-top: 2px;
    }

    .description {
      color: rgba(255, 255, 255, 0.6);
      font-size: 13px;
      margin-bottom: 8px;
    }
  `

  @state() private shareEmail = false
  @state() private sharePhone = false
  @state() private shareEmergency = false
  @state() private userData: UserContactData = {}

  private resolvePromise?: ((choices: ContactShareChoices | null) => void) | undefined
  private animationsConfigured = false

  private configureAnimations(): void {
    if (this.animationsConfigured) return
    const dialog = this.shadowRoot?.querySelector('md-dialog') as MdDialog | null
    if (!dialog) return
    this.animationsConfigured = true

    const dur = 100
    const easing = 'cubic-bezier(0.2, 0, 0, 1)'

    dialog.getOpenAnimation = () => ({
      dialog: [
        [
          [
            { transform: 'scale(0.85)', opacity: 0 },
            { transform: 'scale(1)', opacity: 1 },
          ],
          { duration: dur, easing },
        ],
      ],
      scrim: [[[{ opacity: 0 }, { opacity: 0.32 }], { duration: dur, easing: 'linear' }]],
      container: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur * 0.5, easing: 'linear', pseudoElement: '::before' },
        ],
      ],
      headline: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      content: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      actions: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
    })

    dialog.getCloseAnimation = () => ({
      dialog: [
        [
          [
            { transform: 'scale(1)', opacity: 1 },
            { transform: 'scale(0.9)', opacity: 0 },
          ],
          { duration: 80, easing: 'cubic-bezier(0.4, 0, 1, 1)' },
        ],
      ],
      scrim: [[[{ opacity: 0.32 }, { opacity: 0 }], { duration: 80, easing: 'linear' }]],
      container: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', pseudoElement: '::before' },
        ],
      ],
      headline: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      content: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      actions: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
    })
  }

  protected override firstUpdated(): void {
    this.configureAnimations()
  }

  async show(userData: UserContactData): Promise<ContactShareChoices | null> {
    this.userData = userData
    this.shareEmail = !!userData.email
    this.sharePhone = !!userData.phone
    this.shareEmergency = !!userData.emergencyContact?.name

    const dialog = this.shadowRoot?.querySelector('md-dialog') as MdDialog | null
    if (!dialog) return null
    this.configureAnimations()

    return new Promise<ContactShareChoices | null>(resolve => {
      this.resolvePromise = resolve
      void dialog.show()
    })
  }

  private handleClose(e: Event): void {
    const dialog = e.target as MdDialog
    if (dialog.returnValue === 'share') {
      const choices: ContactShareChoices = {}
      if (this.shareEmail && this.userData.email) choices.email = this.userData.email
      if (this.sharePhone && this.userData.phone) choices.phone = this.userData.phone
      if (this.shareEmergency && this.userData.emergencyContact) {
        choices.emergencyContact = this.userData.emergencyContact
      }
      this.resolvePromise?.(choices)
    } else {
      this.resolvePromise?.(null)
    }
    this.resolvePromise = undefined
  }

  private close(returnValue: string): void {
    void (this.shadowRoot?.querySelector('md-dialog') as MdDialog).close(returnValue)
  }

  override render(): unknown {
    return html`
      <md-dialog @closed=${this.handleClose}>
        <div slot="headline">Share Contact Info</div>
        <div slot="content">
          <div class="description">
            Choose what to share with plan members. This info will be available offline during your
            expedition.
          </div>

          <div class="share-option">
            <div class="option-info">
              <div class="option-label">Email</div>
              ${this.userData.email
                ? html`<div class="option-value">${this.userData.email}</div>`
                : html`<div class="option-missing">Not set in profile</div>`}
            </div>
            ${this.userData.email
              ? html`<md-switch
                  ?selected=${this.shareEmail}
                  @change=${(e: Event) =>
                    (this.shareEmail = (
                      e.target as HTMLInputElement & { selected: boolean }
                    ).selected)}
                ></md-switch>`
              : nothing}
          </div>

          <div class="share-option">
            <div class="option-info">
              <div class="option-label">Phone</div>
              ${this.userData.phone
                ? html`<div class="option-value">${this.userData.phone}</div>`
                : html`<div class="option-missing">Not set in profile</div>`}
            </div>
            ${this.userData.phone
              ? html`<md-switch
                  ?selected=${this.sharePhone}
                  @change=${(e: Event) =>
                    (this.sharePhone = (
                      e.target as HTMLInputElement & { selected: boolean }
                    ).selected)}
                ></md-switch>`
              : nothing}
          </div>

          <div class="share-option">
            <div class="option-info">
              <div class="option-label">Emergency Contact</div>
              ${this.userData.emergencyContact?.name
                ? html`<div class="option-value">
                    ${this.userData.emergencyContact.name}
                    (${this.userData.emergencyContact.relationship})
                  </div>`
                : html`<div class="option-missing">Not set in profile</div>`}
            </div>
            ${this.userData.emergencyContact?.name
              ? html`<md-switch
                  ?selected=${this.shareEmergency}
                  @change=${(e: Event) =>
                    (this.shareEmergency = (
                      e.target as HTMLInputElement & { selected: boolean }
                    ).selected)}
                ></md-switch>`
              : nothing}
          </div>
        </div>
        <div slot="actions">
          <md-text-button @click=${() => this.close('cancel')}>Skip</md-text-button>
          <md-filled-button @click=${() => this.close('share')}>Share</md-filled-button>
        </div>
      </md-dialog>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'contact-share-dialog': ContactShareDialog
  }
}
